import * as R from 'ramda';
import type {
  Configurator,
  SystemQuoteSpecs, SystemSpec,
  TemplateCategory,
  TemplateCategoryComponent
} from '@/types/exceleraSystems';
import {allowNoneCategories } from '@/components/helpers/configuratorConstants.ts';
import { transformTemplateCategory } from '@/server/data/systemConfigurator';
import { GST_MULTIPLIER } from '@/config';

export const generateSystemQuote = (configurator: Configurator): SystemQuoteSpecs => {
  const systemSpecs = configurator.template.categories.reduce((specs: SystemSpec[], category: TemplateCategory): SystemSpec[] => {
    const selectedComponent = category.components.find((component: TemplateCategoryComponent) => component.isSelected);
    if(selectedComponent) {
      const { subtotal, product } = selectedComponent;
      const qty = category.qty || 1;
      const cp = selectedComponent.product.cp || 0; // Default to 0 if no cost price is provided
      return [...specs, {
        product: product,
        category: {
          _id:  category.category._id,
          name: category.category.name
        },
        qty,
        cp,
        subtotal,
        showSp: false,
      }];
    }
    return specs;
  }, []);

  return {
    systemTypeId: configurator.systemDetails._id,
    specifications: systemSpecs,
    qty: 1,
  }
}

export const calcMarginMultiplier = (margin: number): number => {
  return 1 + (margin/100);
}

export function allowNone(category: TemplateCategory): boolean {
  const { category: { name }} = category;
  return allowNoneCategories.includes(name) && category.components.length > 0;
}

export const calcTotal = (configurator: Configurator): number => {
  return configurator.template.categories.reduce((currentTotalAsCents: number, category: TemplateCategory) => {
    const selectedComponent = category.components.find((component: TemplateCategoryComponent)=> {
      return component.isSelected !== undefined && component.isSelected === true;
    });
    const qty = category.qty || 1;
    const subtotalAsDollars = selectedComponent?.subtotal || 0;
    return currentTotalAsCents + Math.round(subtotalAsDollars*qty);
  }, 0);
}

export const calcPriceDiffs = (configurator: Configurator) => {
  // Mutation Warning
  configurator.template.categories = configurator.template.categories.map(transformTemplateCategory);
}

export const getMaxQty = (category: TemplateCategory): number | undefined => {
  const maxQty = category.maxQty || 1;

  return maxQty > 1 ? maxQty : undefined;
}

export function generateQtys(maxQty: number | null): string[] {
  const qtys: string[] = [];
  if(maxQty) {
    for(let i = 1; i <= maxQty; i++) {
      qtys.push(i.toString());
    }
  }
  return qtys;
}

export const calcComponentSubtotal: (markup: number, margin: number) => (component: TemplateCategoryComponent | SystemSpec) => number = (markup, margin) => (component) => {
  const cp = R.pathOr(0, ['product', 'cp'])(component);

  return Math.round(cp*calcMarginMultiplier(margin)*GST_MULTIPLIER);
}